import React, { createContext, useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Events from './pages/Events';
import Privacy from './pages/Privacy';
import Members from './pages/Members';
import ConvoyItem from './pages/ConvoyItem';
import ScrollToTop from './components/ScrollToTop';
import Footer from './components/Footer';
import News from './pages/News';
import NewsItem from './pages/NewsItem';
import NotFoundPage from './pages/NotFoundPage';
import Sponsors from './pages/Sponsors';
import Support from './pages/Support';
import Navigation from './components/Navigation';
import BackToTop from './components/BackToTop';
import CookieConsent from './components/CookieConsent';

import './i18n/i18n';
import { useTranslation } from 'react-i18next';
import Logo from './assets/img/logo.svg';

// Create a context for the loading state
const LoadingContext = createContext();

function LoadingProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [websiteReady, setWebsiteReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      // Small delay before website animation
      setTimeout(() => {
        setWebsiteReady(true);
      }, 500);
    }, 3000); // 3 seconds loading screen

    return () => clearTimeout(timer);
  }, []);

  return (
    <LoadingContext.Provider value={{ loading, websiteReady }}>
      {children}
    </LoadingContext.Provider>
  );
}

function useLoading() {
  const context = useContext(LoadingContext);

  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }

  return context;
}

// Enhanced Loading Screen Component
function EnhancedLoader() {
  const { t } = useTranslation();
  
  return (
    <div className="enhanced-preloader">
      <div className="logo-container">
        <img 
          src={'https://hub.mileageriot.com/assets/images/logo_sm.png'} 
          alt={t('header.logoAlt')} 
          className="preloader-logo"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://hub.mileageriot.com/assets/images/logo_sm.png';
          }}
        />
        <h1 className="company-name">Mileage <span className='text-primary'>Riot</span></h1>
        <div className="loading-animation">
          <div className="loading-bar">
            <div className="loading-progress"></div>
          </div>
          <p className="loading-text">{t('loading.text')}</p>
        </div>
      </div>
      <div className="preloader-particles">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={`particle particle-${i + 1}`}></div>
        ))}
      </div>
    </div>
  );
}

function App() {
  return (
    <LoadingProvider>
      <LoadingWrapper />
    </LoadingProvider>
  );
}

// Modified Home component wrapper to handle scroll after navigation
function HomeWithScroll() {
  const location = useLocation();
  
  useEffect(() => {
    if (location.state?.scrollToPartners) {
      const partnersSection = document.getElementById('partners');
      if (partnersSection) {
        partnersSection.scrollIntoView({ behavior: 'smooth' });
      }
      // Clean up the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  return <Home />;
}

function LoadingWrapper() {
  const { loading, websiteReady } = useLoading();

  return (
    <div>
      {loading ? (
        <EnhancedLoader />
      ) : (
        <React.StrictMode>
          <div className={`website-container ${websiteReady ? 'website-ready' : ''}`}>
            <Router>
              <Navigation />
              <div>   
                <ScrollToTop />
                <main className="main-content">
                  <Routes>
                    <Route path='/' element={<HomeWithScroll />} />
                    <Route path='/events' element={<Events />} />
                    <Route path='/news' element={<News />} />
                    <Route path="/news/:slug" element={<NewsItem/>} />
                    <Route path="/events/:id" element={<ConvoyItem/>} />
                                        <Route path='/privacy' element={<Privacy />} />
                    <Route path='/staff' element={<Members />} />
                    <Route path='/sponsors' element={<Sponsors />} />
                    <Route path='/support' element={<Support />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </main>
                <Footer />
                <BackToTop />
                <CookieConsent />
              </div>
            </Router>
          </div>
        </React.StrictMode>
      )}
    </div>
  );
}

export default App;